if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.viewer) return;
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (!this.elements.viewer) return;
        // Variant updates can insert or replace slides without replacing this gallery.
        this.imageObserver = new MutationObserver(() => this.startImageWarmup());
        this.imageObserver.observe(this.elements.viewer, { childList: true, subtree: true });
        this.startImageWarmup();
      }

      disconnectedCallback() {
        this.imageObserver?.disconnect();
        this.imageWarmupController?.abort();
        this.announcementController?.abort();
        window.clearTimeout(this.announcementTimer);
      }

      startImageWarmup() {
        this.imageWarmupController?.abort();
        this.imageWarmupController = new AbortController();
        this.warmGalleryImages(this.imageWarmupController.signal);
      }

      async warmGalleryImages(signal) {
        // Use the actual responsive <img> elements. Do not fetch their large
        // fallback src URLs separately or include the zoom modal's images.
        const images = Array.from(this.elements.viewer.querySelectorAll('.product__media img'));
        const firstImage = this.elements.viewer.querySelector('.is-active .product__media img') || images[0];
        if (!firstImage || signal.aborted) return;

        // Give the visible image a head start. Errors/timeouts must not stall
        // the rest of the gallery indefinitely.
        await this.loadGalleryImage(firstImage, 'high', signal);
        for (const image of images) {
          if (signal.aborted || !this.isConnected) return;
          if (image === firstImage || !this.elements.viewer.contains(image)) continue;
          await this.loadGalleryImage(image, 'low', signal);
        }
      }

      loadGalleryImage(image, priority, signal) {
        return new Promise((resolve) => {
          if (signal.aborted) return resolve();
          let timer;
          const finish = () => {
            window.clearTimeout(timer);
            image.removeEventListener('load', finish);
            image.removeEventListener('error', finish);
            signal.removeEventListener('abort', finish);
            // Prepare decoded pixels when supported, without hiding the image
            // or making the queue depend on decoding succeeding.
            if (!signal.aborted && image.complete && image.naturalWidth && image.decode) {
              image.decode().catch(() => {});
            }
            resolve();
          };
          image.addEventListener('load', finish, { once: true });
          image.addEventListener('error', finish, { once: true });
          signal.addEventListener('abort', finish, { once: true });
          timer = window.setTimeout(finish, 10000);
          // Never demote an image the customer has already selected.
          if (image.getAttribute('fetchpriority') !== 'high') image.setAttribute('fetchpriority', priority);
          image.loading = 'eager';
          if (image.complete) finish();
        });
      }

      prioritizeMedia(media) {
        const image = media?.querySelector('.product__media img');
        if (!image) return;
        image.setAttribute('fetchpriority', 'high');
        image.loading = 'eager';
      }

      onSlideChanged(event) {
        this.prioritizeMedia(event.detail.currentElement);
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.prioritizeMedia(activeMedia);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        this.announcementController?.abort();
        this.announcementController = new AbortController();
        window.clearTimeout(this.announcementTimer);
        const announce = () => {
          if (!image.naturalWidth) return;
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          if (window.accessibilityStrings?.imageAvailable) {
            this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          }
          this.announcementTimer = window.setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        if (image.complete) announce();
        else image.addEventListener('load', announce, { once: true, signal: this.announcementController.signal });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
